import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useState, useCallback} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {PURPLE, GRAY, PURPLE_DARK} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

import AIDataEntryPopover from './AIDataEntryPopover';
import {FlexibleDataEntryExperience} from './NotebookAIToolsConstants';
export interface AIDataEntryButtonProps {
  disabled?: boolean;
  entryId: string;
  structuredTable: StructuredTable<unknown, unknown>;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
  undoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  addAIDataEntryRows: (
    initialPromptText?: string,
    initialSelectedTableName?: string,
    result?: AddStructuredTableRowsUsingAIResult | null
  ) => void;
}

export default function AIDataEntryButton({
  disabled = false,
  entryId,
  structuredTable,
  addAIDataEntryRows,
  className,
  dataTestAttrs,
  undoState,
}: AIDataEntryButtonProps): JSX.Element {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasSuccessfulSubmission, setHasSuccessfulSubmission] = useState(false);
  assert(addAIDataEntryRows != null, 'addAIDataEntryRows is required');
  const handleUndo = useCallback(() => {
    const selectionData = selutil.getSelectionIndices();
    undoState({selection: selectionData});
  }, [undoState]);

  const handleSubmit = useCallback(
    (result: AddStructuredTableRowsUsingAIResult | null): void => {
      addAIDataEntryRows(undefined, undefined, result);
    },
    [addAIDataEntryRows]
  );

  const handleCancel = useCallback(() => {
    setIsPopoverOpen(false);
    trackEvent(ELN_ANALYTICS_ACTIONS.CLOSED_FLEXIBLE_DATA_ENTRY_MODAL, {
      experience: FlexibleDataEntryExperience.POPOVER,
      numFiles: 0,
      [getEntryOrFileIdKey(entryId, true)]: entryId,
    });
    if (hasSuccessfulSubmission) {
      // If the user has successfully added rows and then closed the popover, it means that they accepted
      // the results.
      trackEvent(ELN_ANALYTICS_ACTIONS.ACCEPTED_FLEXIBLE_DATA_ENTRY_RESULT, {
        numFiles: 0,
        wasVerified: null,
        entryId,
        experience: FlexibleDataEntryExperience.POPOVER,
        isMultiTable: false,
      });
    }
  }, [entryId, hasSuccessfulSubmission]);

  const handleClick = useCallback(
    (e: ElementMouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsPopoverOpen(true);
      }
    },
    [disabled]
  );

  return (
    <Tooltip text="Add rows with AI" placement={FloatingPlacement.TOP}>
      <Popover
        interaction={FloatingInteraction.CONTROLLED}
        isOpen={isPopoverOpen}
        onRequestClose={handleCancel}
        placement={FloatingPlacement.TOP}
        renderContent={() => (
          <AIDataEntryPopover
            entryId={entryId}
            structuredTable={structuredTable}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onUndo={handleUndo}
            hasSuccessfulSubmission={hasSuccessfulSubmission}
            setHasSuccessfulSubmission={setHasSuccessfulSubmission}
            addAIDataEntryRows={addAIDataEntryRows}
          />
        )}
        disableContentSelection={false}
      >
        <span
          className={classNames(css(styles.wrapper), className)}
          onClick={handleClick}
          {...getTestProps(dataTestAttrs)}
        >
          <BenchlingIcon icon="sparkle" className={css(styles.icon, disabled && styles.iconDisabled)} />
        </span>
      </Popover>
    </Tooltip>
  );
}

// These styles won't show up properly yet for exports, but we hide the header icons for tables
// automatically when in viewer mode, so we don't need to worry about it.
const styles = StyleSheet.create({
  wrapper: {
    display: 'inline-block',
    padding: '2px',
    cursor: 'pointer',
    ':hover': {
      cursor: 'pointer',
    },
  },
  icon: {
    height: '12px',
    color: PURPLE,
    transition: 'color 0.2s ease',
    ':hover': {
      color: PURPLE_DARK,
    },
    ':focus': {
      color: PURPLE_DARK,
    },
  },
  iconDisabled: {
    color: GRAY,
    cursor: 'not-allowed',
    ':hover': {
      color: GRAY,
    },
    ':focus': {
      color: GRAY,
    },
  },
});
